import React, { useEffect, useState } from "react";
import { employees, getEmployeeName } from "../../Config/Employees";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";

export const EmployeeAppraisals = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const user = localStorage.getItem("logged_in_email") || "";
  const { isAdmin } = getUserAccess();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [appraisals, setAppraisals] = useState<any[]>([]);
  const [filterEmail, setFilterEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    appraisalDate: "",
    notes: "",
  });


  const fetchAppraisals = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getAppraisals(
        token,
        user,
        isAdmin ? filterEmail : ""
      );
      if (res.data?.data) {
        setAppraisals(res.data.data);
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.notes || (!editingId && (!formData.email || !formData.appraisalDate))) {
      toast({
        position: "top",
        title: "Please fill all required fields",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        await EmployeeDataService.updateAppraisal(
          token,
          editingId,
          { notes: formData.notes },
          user
        );
        toast({
          position: "top",
          title: "Appraisal notes updated",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        await EmployeeDataService.createAppraisal(token, { ...formData, name: getEmployeeName(formData.email) }, user);
        toast({
          position: "top",
          title: "Appraisal notes added",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      }
      onModalClose();
      setFormData({ email: "", appraisalDate: "", notes: "" });
      setEditingId(null);
      fetchAppraisals();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed",
        description: e.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (appraisal: any) => {
    setEditingId(appraisal._id);
    setFormData({
      email: appraisal.email,
      appraisalDate: appraisal.appraisalDate || "",
      notes: appraisal.notes || "",
    });
    onModalOpen();
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData({ email: "", appraisalDate: "", notes: "" });
    onModalOpen();
  };

  useEffect(() => {
    fetchAppraisals();
  }, []);

  return (
    <HrmsPageShell title="Appraisals" isLoading={isLoading}>
      <>
        {/* Admin Controls */}
        {isAdmin && (
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={6}>
            <Button
              bg="#327171"
              color="white"
              onClick={openCreateModal}
              _hover={{ bg: "#285e5e" }}
            >
              Add Appraisal Notes
            </Button>
            <Flex alignItems="flex-end" justifyContent="end" gap={8} flexWrap="wrap">
              <Flex display="flex" alignItems="flex-end" w="300px">
                <Select
                  placeholder="Select Employee"
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                  color="#327171"
                  borderRightRadius={0}
                  focusBorderColor="teal"
                  borderWidth="0.2px"
                  _focus={{ borderWidth: "0.2px", borderColor: "teal.200" }}
                >
                  {employees.map((emp) => (
                    <option key={emp.value} value={emp.value}>
                      {emp.label}
                    </option>
                  ))}
                </Select>
                <Button
                  size="md"
                  borderRadius="md"
                  borderLeftRadius={0}
                  p={5}
                  colorScheme="whiteAlpha"
                  bg="#327171"
                  onClick={fetchAppraisals}
                >
                  Search
                </Button>
              </Flex>
            </Flex>
          </Flex>
        )}

        {/* Appraisals Table */}
        <div className="main-section" style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                {isAdmin && <th>Employee</th>}
                <th>Date</th>
                <th>Notes</th>
                {isAdmin && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {appraisals.length > 0 ? (
                appraisals
                  .sort((a: any, b: any) => {
                    const dateA = a.appraisalDate || a.createdAt || "";
                    const dateB = b.appraisalDate || b.createdAt || "";
                    return dateB.localeCompare(dateA);
                  })
                  .map((appraisal: any) => (
                    <tr key={appraisal._id}>
                      {isAdmin && <td>{appraisal.name || appraisal.email}</td>}
                      <td>
                        {appraisal.appraisalDate
                          ? DateTime.fromISO(appraisal.appraisalDate).toFormat("dd MMM yyyy")
                          : appraisal.createdAt
                          ? DateTime.fromISO(appraisal.createdAt).toFormat("dd MMM yyyy")
                          : "-"}
                      </td>
                      <td style={{ whiteSpace: "pre-wrap" }}>
                        {appraisal.notes || "No notes"}
                      </td>
                      {isAdmin && (
                        <td>
                          <Button
                            size="xs"
                            bg="#327171"
                            color="white"
                            _hover={{ bg: "#285e5e" }}
                            onClick={() => openEditModal(appraisal)}
                          >
                            Update
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 4 : 2} style={{ textAlign: "center", color: "#a0aec0" }}>
                    No appraisal records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Modal */}
        <Modal isOpen={isModalOpen} onClose={onModalClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{editingId ? "Edit Appraisal Notes" : "Add Appraisal Notes"}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {!editingId && (
                <>
                  <FormControl mb={3} isRequired>
                    <FormLabel>Employee</FormLabel>
                    <Select
                      placeholder="Select Employee"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    >
                      {employees.map((emp) => (
                        <option key={emp.value} value={emp.value}>
                          {emp.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl mb={3} isRequired>
                    <FormLabel>Appraisal Date</FormLabel>
                    <Input
                      type="date"
                      value={formData.appraisalDate}
                      onChange={(e) => setFormData({ ...formData, appraisalDate: e.target.value })}
                    />
                  </FormControl>
                </>
              )}
              <FormControl mb={3} isRequired>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Appraisal notes..."
                  rows={5}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onModalClose}>Cancel</Button>
              <Button
                bg="#327171"
                color="white"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                _hover={{ bg: "#285e5e" }}
              >
                {editingId ? "Update" : "Submit"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    </HrmsPageShell>
  );
};
